import { useCallback, useEffect, useRef, useState } from 'react';
import { isShellOffline, SHELL_CONNECTIVITY_EVENT } from '@/shell/shellOffline';

/** The device's connection as the page knows it: the browser's, and the app shell's own signal. */
function deviceOnline(): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  return !isShellOffline();
}

/**
 * I-1491: whether the device has a connection. Both signals the product already has — `online` /
 * `offline` on the window, and the shell's `connectivity` event (F-0236) — so a list screen needs
 * neither of its own.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(deviceOnline);
  useEffect(() => {
    const update = () => setOnline(deviceOnline());
    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    window.addEventListener(SHELL_CONNECTIVITY_EVENT, update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      window.removeEventListener(SHELL_CONNECTIVITY_EVENT, update);
    };
  }, []);
  return online;
}

/**
 * I-1491 (RD-37, RD-38): a retry that does not fire a read which cannot succeed. While `active` and
 * the device is offline, `press` only marks that the reader wants it (`waiting`); no read is sent.
 * When the connection returns, `retry` runs once, pressed or not — the failure being shown was the
 * connection, and the reader should not have to come back to it.
 */
export function useRetryOnReturn(
  active: boolean,
  retry: () => void,
): { online: boolean; waiting: boolean; press: () => void } {
  const online = useOnline();
  const [waiting, setWaiting] = useState(false);
  const retryRef = useRef(retry);
  retryRef.current = retry;
  const wasOnline = useRef(online);
  useEffect(() => {
    const returned = online && !wasOnline.current;
    wasOnline.current = online;
    if (!returned) return;
    setWaiting(false);
    if (active) retryRef.current();
  }, [online, active]);
  const press = useCallback(() => {
    if (online || !active) retryRef.current();
    else setWaiting(true);
  }, [online, active]);
  return { online, waiting: waiting && !online, press };
}
